import { sub } from "../shared/fixed";
import { BUTTON } from "../sim";
import { facedBits, gesture, KEY, mash, run, tap, type Track } from "./inputTrack";
import type { Scenario } from "./scenario";

/**
 * Лист приёмов (GAME-31): каждая кнопка и связка — своя сцена на 2 с с подписью «кнопка → что должно быть».
 * Игрок 1 жмёт по записи, игрок 2 стоит манекеном на нужной дистанции. Съёмка — tools/capture/move-sheet.sh.
 */
const T = 75; // первое нажатие: запись идёт с 70-го кадра боя, когда надпись «БОЙ» уже ушла

interface SheetOptions {
  /** Здоровье соперника в начале сцены — показ решающего удара и полосы жизни. */
  readonly otherHealth?: number;
  /** Показ авто-издёвки: игрок 1 сам дразнит после 2 с без ввода (в остальных сценах — нет, чтобы не путать). */
  readonly autoTaunt?: boolean;
  /** Ярость игрока 1: 1000 — одно деление (суперсилы на две кнопки). */
  readonly meter?: number;
  /** Нажатия игрока 2 — когда сцена показывает защиту. */
  readonly second?: Track;
}

function sheet(caption: string, track: Track, gap = 95, x = 300, options: SheetOptions = {}): Scenario {
  return {
    caption,
    setup: (state) => {
      const [me, other] = state.fighters;
      me.x = sub(x);
      me.facing = 1;
      me.meter = options.meter ?? 0;
      other.x = sub(x + gap);
      other.facing = -1;
      // консультант 04.11.2026: в показе манекен стоит, сам не дразнит — зритель видит только приём игрока 1
      other.quiet = -1_000_000;
      if (options.otherHealth !== undefined) other.health = options.otherHealth;
      if (!options.autoTaunt) me.quiet = -1_000_000;
    },
    input: (frame, state) => [
      facedBits(track, frame, state.fighters[0].facing),
      options.second ? facedBits(options.second, frame, state.fighters[1].facing) : 0,
    ],
  };
}

const { F, B, D, U, P, HP, K, HK, BL, TA, GR } = KEY;
/** Оба курка суперприёма вместе: L2 + R2. */
const BUTTON_SUPER = BUTTON.super | BUTTON.enhance;

/** Подсечка игрока 1 — сбивает второго на пол, дальше сцена показывает, как он встаёт в бой. */
const SWEEP_IN: Track = [[T - 5, T + 10, KEY.D], tap(T + 2, KEY.D | KEY.HK)];

export const MOVE_SHEET: Readonly<Record<string, Scenario>> = {
  "move-01-jab": sheet("□ → джеб: быстрый прямой, кулак вперёд на 4-м кадре, быстрый возврат", [
    tap(T, P),
    tap(T + 30, P),
    tap(T + 60, P),
  ]),
  "move-02-hook": sheet("△ → хук: тяжелее и медленнее джеба", [tap(T, HP), tap(T + 50, HP)]),
  "move-03-kick": sheet("✕ → боковой ногой в корпус: быстрый, средний", [tap(T, K), tap(T + 40, K)]),
  "move-04-high-kick": sheet("○ → высокий ногой в голову: тяжёлый, дальше и медленнее ✕", [tap(T, HK)], 120),
  "move-05-crouch": sheet("↓ → присед в стойке, кулаки у лица", [[T, T + 75, D]], 160),
  "move-06-crouch-jab": sheet("↓ + □ → тычок из приседа", [[T - 5, T + 70, D], tap(T + 5, D | P), tap(T + 35, D | P)]),
  "move-07-uppercut": sheet("↓ + △ → апперкот: подбрасывает соперника", [[T - 5, T + 10, D], tap(T + 2, D | HP)], 80),
  "move-08-sweep": sheet(
    "↓ + ○ → подсечка подкатом: низко, сбивает с ног",
    [[T - 5, T + 10, D], tap(T + 2, D | HK)],
    150,
  ),
  "move-09-walk-jab": sheet(
    "→ шаг + □ → джеб на ходу: боец идёт и бьёт, не замирая",
    [[T, T + 80, F], tap(T + 15, F | P), tap(T + 40, F | P), tap(T + 65, F | P)],
    230,
    200,
  ),
  "move-10-run-punch": sheet(
    "→ → бег + □ → удар с разбега: разгон не пропадает",
    [...run(T - 20, T + 45), tap(T + 28, F | P)],
    330,
    150,
  ),
  "move-11-run-slide": sheet(
    "→ → бег + ✕ → подкат с бега: ногами вперёд, сбивает",
    [...run(T - 20, T + 30), tap(T + 22, F | K)],
    330,
    150,
  ),
  "move-12-run-knee": sheet(
    "→ → бег + ○ → колено с разбега в прыжке: подброс",
    [...run(T - 20, T + 30), tap(T + 22, F | HK)],
    330,
    150,
  ),
  "move-13-back-kick": sheet("← шаг назад + ✕ → удар на отходе", [[T, T + 70, B], tap(T + 1, B | K)], 85, 400),
  "move-14-air": sheet("↑→ прыжок, в прыжке ✕ → удар ногой в воздухе", [tap(T, U | F), tap(T + 18, K)], 230),
  "move-15-chain": sheet("□ □ △ → серия с отменами: удары связываются без пауз", [
    tap(T, P),
    tap(T + 6, P),
    tap(T + 12, HP),
  ]),
  "move-17-series": sheet("□ □ □ □ → авто-серия: джеб, кросс, хук, добивающий апперкот", [
    tap(T, P),
    tap(T + 7, P),
    tap(T + 15, P),
    tap(T + 24, P),
  ]),
  "move-18-dash": sheet("→ + △ → рывок через полэкрана с ударом", [tap(T, F | HP)], 260, 200),
  "move-19-flip": sheet("← + ○ → сальто с ударом пятками сверху", [tap(T, B | HK)], 110),
  "move-20-blink": sheet("← → касаниями → скачок за спину сопернику (деление Ярости)", gesture(T, [B, F]), 140, 300, {
    meter: 1000,
  }),
  "move-21-vanish": sheet("← ← ← касаниями → невидимка на 2,5 с (деление Ярости)", gesture(T, [B, B, B]), 160, 300, {
    meter: 1000,
  }),
  "move-22-block": sheet("R2 → блок: руки к голове; удар в блок — отдача в защите", [[T - 10, T + 80, BL]], 95, 300, {
    second: [tap(T + 10, P), tap(T + 40, HK)],
  }),
  "move-23-charge-punch": sheet("зажать □ и отпустить → заряженный прямой: частицы на заряде, рывок, сбивает", [
    [T - 30, T + 4, P],
  ]),
  "move-24-charge-kick": sheet("зажать ○ и отпустить → заряженный хай-кик: подбрасывает", [[T - 30, T + 4, HK]], 200),
  "move-25-kick-series": sheet("✕ ✕ ✕ → серия ногами: боковой, прямой ногой, вертушка с подбросом", [
    tap(T, K),
    tap(T + 10, K),
    tap(T + 21, K),
  ]),
  "move-26-heavy-series": sheet(
    "○ ○ → серия: высокий в голову, разворот ногой — сбивает с ног",
    [tap(T, HK), tap(T + 17, HK)],
    120,
  ),
  "move-27-step-kick": sheet("→ + ✕ → прямой ногой с шагом: дальше бокового", [tap(T, F | K)], 150),
  "move-28-air-punch": sheet("↑→ прыжок, в прыжке □ → кулак сверху", [tap(T, U | F), tap(T + 20, P)], 230),
  "move-29-rise-roll": sheet("сбит, без ввода → кувырок назад со скольжением и сразу стойка", SWEEP_IN, 150, 300, {}),
  "move-30-rise-forward": sheet("сбит, держит → → кувырок вперёд под соперника", SWEEP_IN, 150, 300, {
    second: [[T + 6, T + 50, F]],
  }),
  "move-31-rise-kip": sheet("сбит, ↑ → подъём прыжком сразу в воздух", SWEEP_IN, 150, 300, {
    second: [[T + 16, T + 40, U]],
  }),
  "move-32-rise-attack": sheet("сбит, жмёт удар → атака с пола: удары ногами снизу и выпад", SWEEP_IN, 150, 300, {
    second: [[T + 8, T + 30, HK]],
  }),
  "move-33-crouch-block": sheet(
    "↓ + R2 → блок в приседе: держит подсечку, отдача в защите сидя",
    [[T - 10, T + 80, D | BL]],
    140,
    300,
    {
      second: [[T + 5, T + 20, D], tap(T + 8, D | HK)],
    },
  ),
  "move-34-rage-aura": sheet(
    "полная Ярость → огненная аура вокруг бойца: суперприём готов (L2 + R2)",
    [[T, T + 60, F]],
    240,
    250,
    {
      meter: 3000,
    },
  ),
  "move-35-blink-back": sheet(
    "← → скачок за спину → соперник ещё спиной: удар в спину не блокируется, потом разворот",
    [...gesture(T, [B, F]), tap(T + 37, P), tap(T + 47, P)],
    120,
    300,
    {
      meter: 1000,
      second: [[T - 10, T + 90, BL]],
    },
  ),
  "move-36-elbow-lunge": sheet("← + □ → локоть с выпадом: глубокий шаг, сильный удар", [tap(T, B | P)], 120),
  "move-37-magic-shot": sheet(
    "← + △ → магический выстрел: замах двумя руками, сгусток летит через арену",
    [tap(T, B | HP)],
    330,
    200,
  ),
  "move-38-drop-kick": sheet("→ + ○ → дропкик: прыжок, удар двумя ногами, сбивает", [tap(T, F | HK)], 170),
  "move-39-blink-counter": sheet(
    "контрприём: соперник видит замах скачка, жмёт назад + R2 — встречает лицом и блокирует",
    [...gesture(T, [B, F]), tap(T + 37, P)],
    120,
    300,
    {
      meter: 1000,
      second: [
        [T + 7, T + 31, B | BL],
        [T + 31, T + 75, F | BL],
      ],
    },
  ),
  "move-40-mash-square": sheet(
    "□ 8 раз быстро → серия разных ударов, а не одно движение",
    mash(T, [P, P, P, P, P, P, P, P]),
    95,
    250,
  ),
  "move-41-mash-mix": sheet(
    "кнопки вперемешку быстро → удары идут один за другим, каждый свой",
    mash(T, [P, K, HP, HK, P, P, K, HK, HP, P], 8, 4),
    100,
    250,
  ),
  "move-42-combo-ppt": sheet("□ □ △ → джеб, кросс, финал «локоть сверху» с огнём", mash(T, [P, P, HP], 11), 95, 250),
  "move-43-combo-pko": sheet("□ ✕ ○ → джеб, боковой ногой, финал «разворот ногой»", mash(T, [P, K, HK], 11), 95, 250),
  "move-44-combo-too": sheet(
    "△ ○ ○ → хук, ногой в голову, финал «разворот ногой»",
    mash(T, [HP, HK, HK], 13),
    100,
    250,
  ),
  "move-45-combo-kkt": sheet("✕ ✕ △ → боковой, прямой ногой, финал «локоть сверху»", mash(T, [K, K, HP], 11), 100, 250),
  "move-46-rising-strike": sheet(
    "↓ ↑ касаниями → «Взлёт»: прыжок вверх с огненным апперкотом, сбивает прыгнувшего",
    gesture(T + 4, [D, U]),
    90,
    300,
    { second: [tap(T, U)] },
  ),
  "move-47-mash-live": sheet(
    "□ живым темпом, раз в 0,25 с → серия до финала, потом новая серия",
    mash(T, [P, P, P, P, P, P, P, P, P, P], 15, 6),
    95,
    250,
  ),
  "move-48-squall": sheet(
    "L2 + R2 при полной Ярости → «Шквал»: наезд камеры, серия ударов, финал — бросок через арену",
    [[T, T + 3, BUTTON_SUPER]],
    110,
    250,
    { meter: 3000 },
  ),
  "move-49-crouch-all": sheet(
    "держит ↓: □ тычок, ✕ низкий по ногам, ○ подсечка, △ апперкот — у каждой кнопки свой удар",
    [[T - 10, T + 150, D], tap(T, D | P), tap(T + 30, D | K), tap(T + 62, D | HK), tap(T + 110, D | HP)],
    110,
    250,
  ),
  "move-50-close-elbow": sheet("→ + □ вплотную → локоть: короткий, сильный", [tap(T, F | P)], 70, 300),
  "move-51-back-run": sheet("← ← → бег назад от соперника, в конце разворот лицом", gesture(T, [B, B]), 120, 450),
  "move-52-taunt": sheet(
    "тачпад / T → издёвка: копит Ярость, открывает для удара; любая кнопка прерывает",
    [tap(T, TA), tap(T + 150, P)],
    260,
    250,
  ),
  "move-53-trip": sheet("↓ + ○ подсечка → соперник спотыкается и кувыркается", SWEEP_IN, 150, 300),
  "move-54-auto-taunt": sheet("2 с без ввода → боец сам дразнит соперника", [], 260, 250, { autoTaunt: true }),
  "move-55-throw": sheet(
    "L1 вплотную → бросок через плечо: соперник перелетает и падает за спиной",
    [tap(T, GR)],
    80,
    400,
  ),
  "move-56-throw-tech": sheet("захват, соперник жмёт L1 сразу → вырвался, оба отскочили", [tap(T, GR)], 80, 400, {
    second: [tap(T + 5, GR)],
  }),
  "move-60-cancel-jab-shot": sheet(
    "□, сразу ← + △ → джеб прерван магическим выстрелом",
    [tap(T, P), tap(T + 7, B | HP)],
    120,
    250,
  ),
  "move-61-cancel-hook-back": sheet(
    "△, сразу ← ← → хук прерван бегом назад",
    [tap(T, HP), ...gesture(T + 13, [B, B])],
    95,
    400,
  ),
  "move-62-cancel-high-low": sheet(
    "○, сразу ↓ + ✕ → удар в голову прерван низким по ногам",
    [tap(T, HK), [T + 16, T + 44, D], tap(T + 17, D | K)],
    120,
    300,
  ),
  "move-63-cancel-kick-grab": sheet(
    "✕ вплотную, сразу L1 → удар прерван захватом",
    [tap(T, K), tap(T + 10, GR)],
    80,
    400,
  ),
  "move-64-walk-strikes": sheet(
    "шаг вперёд, на ходу □ и ✕ → удары без остановки",
    [[T, T + 40, F], tap(T + 8, F | P), tap(T + 24, F | K)],
    200,
    250,
  ),
  "move-65-taunt-strike": sheet("издёвка, сразу △ → издёвка прервана ударом", [tap(T, TA), tap(T + 40, HP)], 110, 300),
  "move-66-strike-settle": sheet("△ и ничего → удар и мягкий выход в стойку", [tap(T, HP)], 110, 300),
  "move-67-back-series": sheet(
    "держит ← и жмёт □ много раз → своя серия: локоть, кросс, хук, апперкот",
    [[T - 5, T + 100, B], ...mash(T, [B | P, B | P, B | P, B | P, B | P], 15, 6)],
    95,
    350,
  ),
  "move-68-low-series": sheet(
    "держит ↓ и жмёт □ много раз → низкая серия: тычок, по ногам, апперкот, подсечка",
    [[T - 5, T + 100, D], ...mash(T, [D | P, D | P, D | P, D | P, D | P], 15, 6)],
    100,
    300,
  ),
  "move-69-finale": sheet(
    "серия по сопернику с малым здоровьем → полоса вздрагивает, кусок откалывается; решающий удар — замедленно",
    mash(T, [P, P, P, P, P, P], 12, 5),
    95,
    300,
    { otherHealth: 60 },
  ),
  "move-70-decisive-miss": sheet(
    "решающий удар мимо: у соперника мало здоровья, он блокирует — замедление снимается, бой идёт дальше",
    mash(T, [P], 12, 5),
    95,
    300,
    { otherHealth: 20, second: [[T - 10, T + 120, KEY.BL]] },
  ),
  "move-16-cancel": sheet("○, затем → → конец удара отменяется шагом", [tap(T, HK), [T + 26, T + 80, F]], 200),
};
